import { Observable, Subscriber } from 'rxjs';

/**
  A: Source observable type
  B: Subscriber observable type
*/

type ProcessFn<A, B> = {
  (sourceValue: A, subscriber: Subscriber<B>): void;
};

export class GenericOperator<A, B> {
  /**
  The processFn receives the source value (from the previous operator)
  and the subscriber (for calling the next operator).

  Is responsible for calling subscriber.next() (or .error/.completed).
  */
  processFn: ProcessFn<A, B>;

  constructor(processFn: ProcessFn<A, B>) {
    this.processFn = processFn;
  }

  process = (sourceValue: A, subscriber: Subscriber<B>) => {
    this.processFn(sourceValue, subscriber);
  };

  observer = (subscriber: Subscriber<B>) => {
    return {
      complete: () => {
        subscriber.complete();
      },
      error: (err: unknown) => {
        subscriber.error(err);
      },
      next: (sourceValue: A) => {
        this.process(sourceValue, subscriber);
      },
    };
  };

  observable = (source$: Observable<A>) => {
    return new Observable<B>((subscriber) => {
      const subscription = source$.subscribe(this.observer(subscriber));

      return () => {
        subscription.unsubscribe();
      };
    });
  };

  operator = () => {
    return (source$: Observable<A>) => {
      return this.observable(source$);
    };
  };
}
